import React from 'react';
import './validate-schema-n-payload.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Stack from 'react-bootstrap/Stack';
import { Alert } from 'react-bootstrap';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Overlay from 'react-bootstrap/Overlay';
import Tooltip from 'react-bootstrap/Tooltip';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as addFormats from 'ajv-formats';
import { Link } from 'react-router-dom';
import parseJSON from '../../helpers/parse-json';
import prettify from '../../helpers/prettify';
import getAjv from '../../helpers/get-ajv';
import jsonSchemaTemplate from '../../templates/json-schema.json';
import jsonPayloadTemplate from '../../templates/json-payload.json';

const textArea = { width: '800px', margin: '20px', fontFamily: 'Courier New' }
const link = { width: '200px', height: '50px', margin: '10px', padding: '5px', color: 'red', backgroundColor: 'lightgray', textAlign: 'center' }

const jsonSchemaModes = { 'standard': 'name', 'custom': 'function.name' }

function ValidateSchemaNPayload() {
  const showSelectedSchemaTarget = useRef();

  const [alertMsg, setAlertMsg] = useState('');
  const [alertVariant, setAlertVariant] = useState('info');
  const [selectedJsonSchemas, setSelectedJsonSchemas] = useState('[]');
  const [isModeParallel, setIsModeParallel] = useState(true);
  const [showSelectedSchema, setShowSelectedSchema] = useState(false);
  const [errors, setErrors] = useState([]);
  const [functionName, setFunctionName] = useState('');
  const [jsonSchema, setJsonSchema] = useState('');
  const [jsonSchemas, setJsonSchemas] = useState([]);
  const [payload, setPayload] = useState('');
  const [result, setResult] = useState([]);
  const [jsonSchemaMode, setJsonSchemaMode] = useState(jsonSchemaModes.standard); // 'name' || 'function.name'

  const clearOutputs = useCallback(() => {
    setErrors([]);
    setResult([]);
  }, [setErrors, setResult]);

  const compileSchema = useCallback((jsonSchema, mode) => {
    try {
      const ajv = getAjv(mode);
      addFormats(ajv);
      return ajv.compile(jsonSchema)
    }
    catch (error) {
      console.error(error);
      setErrors(errors => [...errors, { message: `Error on compiling schema: ${error?.message}` }]);
      setAlertVariant('danger');
    }
  }, [setErrors])

  const handleValidate = useCallback((mode) => {
    clearOutputs();

    if (!isModeParallel) {
      if (!jsonSchema) {
        setAlertMsg('No JSON schema found!');
        setAlertVariant('warning');
        return;
      }
      const validate = compileSchema(jsonSchema, mode);
      const valid = validate?.((payload.arguments || payload.parameters) ?? payload);

      if (!valid && validate?.errors) {
        setErrors(errors => [...errors, ...validate.errors]);
        console.warn(validate?.errors);
      }

      setResult([valid]);
    }
    else {
      // OpenAI syntax vs TogetherAI syntax
      const tool_key = payload?.tool_uses ? 'tool_uses' : 'tool_use';
      const method_key = payload?.tool_uses ? 'recipient_name' : 'function_name';
      const functions_prefix = payload?.tool_uses ? 'functions.' : '';
      var allResults = [];

      let valid = false;
      let validate = {};

      // OpenAI and TogetherAI Syntax
      if (payload?.[tool_key]) {

        // if it's not array alert
        if (!Array.isArray(payload[tool_key])) {
          const error = 'Payload is not an array, it should be in format! { tool_use: [] }';
          setAlertMsg(error);
          setAlertVariant('danger');
          // setErrors(errors => [...errors, error]);
          return;
        }


        for (let singlePayload of payload[tool_key]) {
          let jsonSchema = undefined;

          if (jsonSchemaMode == jsonSchemaModes.standard) {
            jsonSchema = jsonSchemas.find(js => `${functions_prefix}${js.name}` === singlePayload[method_key]);
          }
          else if (jsonSchemaMode == jsonSchemaModes.custom) {
            jsonSchema = jsonSchemas.find(js => `${functions_prefix}${js.function?.name}` === singlePayload[method_key]);
          }

          if (!jsonSchema) {
            allResults.push(false);
            setErrors(errors => [...errors, `${singlePayload[method_key]} not found!`]);
            continue;
          }

          if (jsonSchemaMode == jsonSchemaModes.standard) {
            validate = compileSchema(jsonSchema.parameters, mode);
          }
          else if (jsonSchemaMode == jsonSchemaModes.custom) {
            validate = compileSchema(jsonSchema.function?.parameters, mode);
          }

          valid = validate?.(singlePayload.arguments || singlePayload.parameters);

          if (!valid) {
            if (validate?.errors) {
              setErrors(errors => [...errors, ...validate.errors]);
              console.error(validate.errors);
            }
            allResults.push(false);
          }
          else {
            allResults.push(true);
            setErrors(errors => [...errors, {}]); // No error is pushed
          }
        }
      }
      else {
        // Custom
        const functionNames = payload?.map?.(p => Object.keys(p)[0]);

        for (let i = 0; i < functionNames?.length; i++) {
          let functionName = functionNames[i];
          const jsonSchema = jsonSchemas.find(js => js.function?.name === functionName);

          if (!jsonSchema) {
            allResults.push(false);
            setErrors(errors => [...errors, `${functionName} not found!`]);
            continue;
          }
          else {
            validate = compileSchema(jsonSchema.function.parameters, mode);
            valid = validate?.(payload[i][functionName]);
          }

          if (!valid) {
            const newErrors = validate?.errors ?? [];
            setErrors(errors => [...errors, ...newErrors]);
            console.warn(validate?.errors);
            allResults.push(false);
          }
          else {
            allResults.push(true);
            setErrors(errors => [...errors, {}]); // No error is pushed
          }
        }
      }
      setResult(allResults);
    }
  }, [isModeParallel, jsonSchema, payload, setResult, setErrors, clearOutputs, jsonSchemas]);

  const checkParsedJSON = useCallback((parsed) => {
    if (!parsed) {
      setAlertMsg('JSON is not parsed!');
      setAlertVariant('danger');
      return false;
    }
    else if (Object.keys(parsed).length === 0) {
      setAlertMsg('JSON is empty!');
      setAlertVariant('warning')
    }

    return true;
  }, []);

  const handleJsonSchemaChange = useCallback((e) => {
    if (!e.target.value) {
      return;
    }

    const parsed = parseJSON(e.target.value);

    if (!checkParsedJSON(parsed)) {
      return;
    }

    clearOutputs();

    prettify('jsonSchemas');

    if (parsed[0].name) {
      setJsonSchemaMode('name');
    }
    else if (parsed[0].function.name) {
      setJsonSchemaMode('function.name');
    }

    const sortAZ = (x, y) => x.name > y.name ? 1 : -1;

    if (parsed.length > 0) {
      setJsonSchemas(parsed.sort(sortAZ));
    }
    else {
      setJsonSchema(parsed.sort(sortAZ));
    }
  }, [setJsonSchemas, clearOutputs]);

  const handlePayloadChange = useCallback((e) => {
    if (!e.target.value) {
      return;
    }

    const parsed = parseJSON(e.target.value);

    if (!checkParsedJSON(parsed)) {
      return;
    }

    clearOutputs();

    prettify('payload');

    setPayload(parsed);
  }, [setPayload, clearOutputs]);

  const handleFunctionNameChange = useCallback((e) => {
    const functName = e.target.value;

    if (!functName) {
      return;
    }

    setFunctionName(functName);

  }, [setFunctionName]);

  useEffect(() => {
    if (!functionName) {
      return;
    }

    let onlyFunctName = '';

    if (functionName.match(/^functions./)) {
      onlyFunctName = functionName.replace(/^functions./, '');
    }
    else {
      onlyFunctName = functionName;
    }

    const jsonSchema = jsonSchemas.find(js => js.name === onlyFunctName) || jsonSchemas.find(js => js.function?.name === onlyFunctName);
    if (jsonSchemas.length > 0 && !jsonSchema) {
      setAlertMsg('Function not found!');
      setAlertVariant('danger');
    }
    else {
      setJsonSchema(jsonSchema);
      setSelectedJsonSchemas(JSON.stringify(jsonSchema, undefined, 4));
    }
  }, [functionName, jsonSchemas])

  useEffect(() => {
    if (jsonSchema?.name && jsonSchema?.parameters) {
      setJsonSchema(jsonSchema.parameters);
    }
    else if (jsonSchema?.type === 'function') {
      setJsonSchema(jsonSchema.function.parameters)
    }
  }, [jsonSchema, setJsonSchema]);

  useEffect(() => {
    if (isModeParallel) {
      // OpenAI syntax vs TogetherAI syntax
      const tool_key = payload?.tool_uses ? 'tool_uses' : 'tool_use';
      const prefix = payload?.tool_uses ? 'functions.' : '';
      const method_key = payload?.tool_uses ? 'recipient_name' : 'function_name';
      if (payload?.[tool_key]) {

        // if it's not array alert
        if (!Array.isArray(payload[tool_key])) {
          const error = 'Payload is not an array, it should be in format! { tool_use: [] }';
          setAlertMsg(error);
          setAlertVariant('danger');
          return;
        }

        let shownSchemas = [];

        for (let singlePayload of payload[tool_key]) {
          const jsonSchema = jsonSchemas.find(js => `${prefix}${js['function']?.name || js.name}` === singlePayload[method_key]);
          if (!shownSchemas.find(x => x === jsonSchema)) {
            shownSchemas.push(jsonSchema);
          }
        }
        setSelectedJsonSchemas(JSON.stringify(shownSchemas, undefined, 4));
      }
    }
  }, [isModeParallel, payload, jsonSchemas, setSelectedJsonSchemas])

  return (
    <Stack gap={3} direction='vertical' autoCorrect='' className="ValidateSchemaNPayload">
      <FloatingLabel as='header' className="ValidateSchemaNPayload-header">
        JSON schema validator
        <Stack direction='horizontal' style={{ alignItems: 'center' }}>
          <Link style={link} to='/'>Main page</Link>
        </Stack>
      </FloatingLabel>

      {
        alertMsg &&
        <Alert variant={alertVariant} dismissible>{alertMsg}</Alert>
      }

      <FloatingLabel as='h4'>
        {isModeParallel ? 'Parallel Functions Call' : 'Single Function Call'}
      </FloatingLabel>

      <Stack direction='horizontal' gap={2} className="col-md-3 mx-auto"  >
        <ButtonGroup style={{ width: '400px' }}
        >
          <ToggleButton
            id="toggle-check"
            type="checkbox"
            variant="secondary"
            checked={isModeParallel}
            value="1"
            onChange={(e) => setIsModeParallel(e.currentTarget.checked)}
          >
            Toggle: Parallel | Single
          </ToggleButton>
        </ButtonGroup>
      </Stack>
      <Stack direction='horizontal' gap={3} className="mx-auto"  >
        <Form.Control id="jsonSchemas" placeholder={'E.g.,\n' + JSON.stringify(jsonSchemaTemplate, undefined, 1)} style={textArea} onBlur={handleJsonSchemaChange} as="textarea" rows={20} />
        <Form.Control id="payload" placeholder={'E.g.,\n' + JSON.stringify(jsonPayloadTemplate, undefined, 1)} style={textArea} onBlur={handlePayloadChange} as="textarea" rows={20} />
      </Stack>
      {
        !isModeParallel &&
        <>
          <Stack direction='horizontal' className="mx-auto">
            <input id="functionName" value={functionName} style={{ width: '400px' }} onChange={handleFunctionNameChange} placeholder='Write function name' />
          </Stack>

          <FloatingLabel as='label' htmlFor="functionNames">
            Or choose a function from list:
          </FloatingLabel>
          <Stack direction='horizontal' className="mx-auto"  >
            <Form.Select style={{ width: '30vw' }} aria-label="Default select example" name="functionNames" id="functionNames" onBlur={handleFunctionNameChange}>
              {
                jsonSchemas[0]?.name &&
                jsonSchemas.map((jsonSchema, idx) => <option key={`fn-${idx}`} value={jsonSchema.name}>{jsonSchema.name} </option>)
              }

              {
                jsonSchemas[0]?.type === 'function' &&
                jsonSchemas.map((jsonSchema, idx) => <option key={`fn-${idx}`} value={jsonSchema.function.name}>{jsonSchema.function.name} </option>)
              }
            </Form.Select>
          </Stack>
        </>
      }
      <Stack direction='horizontal' className="mx-auto">
        <Button onClick={handleValidate} style={{ width: '100px', margin: '0 10px' }}>Validate</Button>
        <Button onClick={() => handleValidate('2019')} variant='secondary' style={{ width: '100px', margin: '0 10px' }}>ajv-2019</Button>
        <Button onClick={() => handleValidate('2020')} variant='secondary' style={{ width: '100px', margin: '0 10px' }}>ajv-2020</Button>
      </Stack>
      <Stack direction='horizontal' className="mx-auto" >
        <Button ref={showSelectedSchemaTarget} onClick={() => setShowSelectedSchema(!showSelectedSchema)}>
          Selected schemas
        </Button>
        <Overlay target={showSelectedSchemaTarget.current} show={showSelectedSchema} placement="top">
          {(props) => (
            <Tooltip id="overlay-example" {...props}>
              <Form.Control id="payload" placeholder='Payload' style={textArea} disabled as="textarea" rows={30} value={selectedJsonSchemas} />

            </Tooltip>
          )}
        </Overlay>
      </Stack>
      <Stack direction='horizontal' className="mx-auto"  >
        <FloatingLabel as='h4' htmlFor='result'> Result: </FloatingLabel>
        <FloatingLabel id='result' as='div'>
          {
            result?.map((res, idx) =>
              res ?
                <span id="result" key={`res-${idx}`} style={{ color: 'green' }}> &#10003; </span>
                :
                <span id="result" key={`res-${idx}`}>  &#10060; </span>
            )
          }
        </FloatingLabel>
      </Stack >

      <Stack direction='horizontal' className="mx-auto"  >
        <FloatingLabel as='h4' htmlFor='errors'> Errors: {errors.filter(err => err && Object.keys(err).length > 0)?.length} </FloatingLabel>
        <FloatingLabel id='errors' as='div'>
          {
            errors?.map((error, idx) => Object.keys(error).length > 0 &&
              <Alert dismissible variant={alertVariant} key={`err-${idx}`}>
                {idx + 1}. {JSON.stringify(error)}
              </Alert>
            )
          }
        </FloatingLabel>
      </Stack>
    </Stack >
  );
}

export default ValidateSchemaNPayload;
